import withDataLoaded from '../with_data_loaded';
import { branchListLoader, licenseListLoader, userLoader } from '../../actions/data_loaders';
import PageSpinner from '../shared/page_spinner';
import { useSelector } from 'react-redux';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import StatesHelper from '../../helpers/states_helper';
import { TabSwapWarningContext } from './components/tab_swap_warning_context';
import FunctionalityAccessHelper from '../../helpers/functionality_access_helper';
import Constants from '../../helpers/constants';
import Actions from '../../actions/actions';
import { UserAdminDataGrid, UserAdminToolbar } from './components/user_admin_data_grid';
import { Box, Button, Checkbox, FormControl, Select } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { isEqual } from '../../helpers/generic_utils';

const companyWide = 'Company Wide';

const defaultCol = { sortable: false };

const removeSpace = (input) => input?.replace(/\s/g, '') ?? '';

const customToolbar = UserAdminToolbar('License List', false, null);

function LicensesDatatable() {
  const licenseList = useSelector((redux) => redux.licenseList);
  const branchList = useSelector((redux) => redux.branchList);
  const currUser = useSelector((redux) => redux.user);
  const coid = useSelector((redux) => redux.view.coid);
  const acls = useSelector((redux) => redux.userFunctionalityAcls);

  const { changeDialogContext, setChangeDialogContext } = useContext(TabSwapWarningContext);

  let [isLoading, setIsLoading] = useState(false);

  const branchNames = branchList.map((b) => b.branchname);
  const getBranchIdFromName = (name) => branchList.find((b) => b.branchname === name)?.id;
  const getBranchNameFromId = (id) => branchList.find((b) => b.id === id)?.branchname;

  let initLicenseRows = StatesHelper.validStates().map((s, index) => {
    let foundLicenses = licenseList?.filter((l) => l.state === s.abbreviation);
    let branches = [companyWide];
    if (foundLicenses && foundLicenses.length !== 0 && !foundLicenses?.filter((l) => !l.branchId)?.length) {
      branches = foundLicenses.map((l) => getBranchNameFromId(l.branchId));
    }
    return { id: index, included: !!foundLicenses?.length, state: s.name, branches };
  });
  const [licenseRows, setLicenseRows] = useState(initLicenseRows);

  /* eslint-disable  react-hooks/exhaustive-deps */
  // Watch for edits
  useEffect(() => {
    setChangeDialogContext({ ...changeDialogContext, isChanges: !isEqual(initLicenseRows, licenseRows) });
  }, [licenseList, licenseRows]);

  useEffect(() => {
    if (changeDialogContext.displayDialog) {
      unsavedChangesModal();
    }
  }, [changeDialogContext]);
  /* eslint-enable  react-hooks/exhaustive-deps */

  const updateRow = useCallback(
    (id, newRow) => setLicenseRows(licenseRows?.map((r) => (r.id === id ? newRow : r))),
    [licenseRows, setLicenseRows]
  );

  const unsavedChangesModal = () =>
    Actions.requestModals({
      title: `You have unsaved changes. Do you want to save changes?`,
      cancelButtonText: 'No, Discard Changes',
      okButtonText: 'Yes, Save',
      modalID: 'qa-licenses-unsaved-changes-modal',
      okID: 'qa-licenses-unsaved-changes-confirm',
      cancelID: 'qa-licenses-unsaved-changes-cancel',
      okProcess: async () => {
        await onSave();
        changeDialogContext?.confirmTabSwap();
      },
      cancelProcess: async () => {
        onCancel();
        changeDialogContext?.confirmTabSwap();
      },
    });

  const discardChangesModal = () =>
    Actions.requestModals({
      title: `Are you sure you want to cancel changes?`,
      cancelButtonText: 'No, Continue Editing',
      okButtonText: 'Yes, Cancel',
      modalID: 'qa-licenses-discard-changes-modal',
      okID: 'qa-licenses-discard-changes-confirm',
      cancelID: 'qa-licenses-discard-changes-cancel',
      okProcess: onCancel,
      cancelProcess: () => {},
    });

  if (currUser === null) {
    return <PageSpinner />;
  }

  const hasACL = FunctionalityAccessHelper.hasACL(acls, Constants.FUNCTIONALITY_ACL_MANAGE_LICENSES);

  const onSave = async () => {
    setIsLoading(true);
    let newLicenses = licenseRows
      .filter((r) => r.included)
      .reduce((acc, cur) => {
        let newL;
        if (cur.branches[0] === companyWide) {
          newL = [
            {
              id: 0,
              branchId: null,
              coid: coid,
              state: StatesHelper.abbrFromName(cur.state),
              licenseNumber: null,
            },
          ];
        } else {
          let state = StatesHelper.abbrFromName(cur.state);
          newL = cur.branches.map((b) => ({
            id: 0,
            branchId: getBranchIdFromName(b),
            coid: coid,
            state,
            licenseNumber: null,
          }));
        }
        return acc.concat(newL);
      }, []);

    let response = await Actions.bulkUpdateLicenses({ coid, newLicenses });
    setIsLoading(false);

    if (!!response && response.includes('fail')) {
      Actions.setErrorMessages(['Problem encountered while saving']);
      return;
    }
    // setIsLoading(false);
    Actions.setMessages(['Changes saved successfully']);
  };

  const onCancel = () => {
    setLicenseRows(initLicenseRows);
  };

  const CustomFooter = () => (
    <Box className="table-footer">
      {changeDialogContext?.isChanges && (
        <Button onClick={discardChangesModal} disabled={isLoading} id="qa-admin-licenses-cancel-button">
          Cancel
        </Button>
      )}
      <Button
        disabled={!hasACL || !changeDialogContext?.isChanges || isLoading}
        variant="contained"
        color="primary"
        id="qa-admin-licenses-save-button"
        onClick={onSave}
      >
        Save
      </Button>
    </Box>
  );

  const checkBoxRender = ({ row }) => (
    <Checkbox
      className={`${row.included ? 'Mui-checked' : ''}`}
      id={`qa-include-${removeSpace(row.state)}`}
      color="primary"
      checked={row.included}
      disabled={!hasACL}
      onChange={(e) => {
        updateRow(row.id, { ...row, included: e.target.checked });
      }}
    />
  );

  const multiSelectRender = ({ row }) => (
    <FormControl size="small" style={{ width: '100%' }}>
      <Select
        labelId={`qa-branches-${removeSpace(row.state)}-label`}
        id={`qa-branches-${removeSpace(row.state)}`}
        multiple
        value={row.branches}
        onChange={({ target: { value } }) => {
          if (value.length === 0 || (value.includes(companyWide) && !row.branches.includes(companyWide))) {
            updateRow(row.id, { ...row, branches: [companyWide] });
          } else {
            updateRow(row.id, { ...row, branches: value.filter((v) => v !== companyWide) });
          }
        }}
        renderValue={(selected) => (row.included ? selected.join(', ') : '')}
        disabled={!row.included || !hasACL}
      >
        <MenuItem key={companyWide} value={companyWide} disabled={!hasACL}>
          <Checkbox
            checked={row.branches.includes(companyWide)}
            color="primary"
            id={removeSpace(`qa-menu-${row.state}-${companyWide}`)}
          />
          <ListItemText primary={companyWide} />
        </MenuItem>
        {branchNames.map((name) => (
          <MenuItem key={name} value={name} disabled={!hasACL}>
            <Checkbox
              checked={row.branches.indexOf(name) > -1}
              color="primary"
              id={removeSpace(`qa-menu-${row.state}-${name}`)}
            />
            <ListItemText primary={name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const columns = [
    { ...defaultCol, field: 'id', hide: true },
    {
      ...defaultCol,
      field: 'included',
      renderHeader: () => <input style={{ display: 'none' }}></input>, //for QA
      maxWidth: 50,
      renderCell: checkBoxRender,
    },
    { ...defaultCol, field: 'state', headerName: 'State', minWidth: 150 },
    {
      ...defaultCol,
      field: 'branches',
      headerName: 'Branches/Sales Office',
      flex: 1,
      renderCell: multiSelectRender,
    },
  ];

  return (
    <div className="user-admin-page datagrid-page" id={'qa-license-list-page'}>
      <UserAdminDataGrid
        columns={columns}
        rows={licenseRows}
        hideFooter={!hasACL}
        components={{
          LoadingOverlay: PageSpinner,
          Toolbar: customToolbar,
          Footer: CustomFooter,
        }}
        sortModel={[{ field: 'state', sort: 'asc' }]}
        rowClassFunc={(row) => `qa-license-list-row-${row.state}`}
      />
    </div>
  );
}

const withLicensesLoaded = withDataLoaded({
  WithData: LicensesDatatable,
  WithoutData: PageSpinner,
  data: [userLoader, branchListLoader, licenseListLoader],
});

export default withLicensesLoaded;
